/**
 * Roster naming shared by the roster comparison controls
 *
 * Revision 0 is the original upload; any other revision is a revised copy, and its
 * revision name is what tells two revisions of the same roster apart.
 */

// Fields needed to label a roster revision (a subset of the provider roster record)
export type RosterLabelFields = {
    revisionId: string;
    rosterName: string;
    revisionName: string;
};

export const ORIGINAL_REVISION_ID = '0';

// Separators in a roster label: "Revised | Network A - Q3 update"
export const KIND_SEPARATOR = ' | ';
export const REVISION_SEPARATOR = ' - ';

// Identifies a roster revision, e.g. as a React key or a dropdown option value
export const rosterKey = (rosterId: string, revisionId: string): string =>
    `${rosterId}-${revisionId}`;

// "Network A" for the original, "Network A - Q3 update" for a revision: how a selected roster
// is named in the comparison summary
export const formatRosterName = ({revisionId, rosterName, revisionName}: RosterLabelFields): string =>
    revisionId !== ORIGINAL_REVISION_ID && revisionName ? `${rosterName}${REVISION_SEPARATOR}${revisionName}` : rosterName;

// The parts of formatRosterLabel's label, for controls that show a shortened form (e.g. the
// roster chips); the original revision has no revision name to show
export const rosterLabelParts = ({revisionId, rosterName, revisionName}: RosterLabelFields) => {
    const original = revisionId === ORIGINAL_REVISION_ID;
    return {
        kind: original ? 'Original' : 'Revised',
        rosterName,
        revisionName: original ? '' : revisionName,
    };
};

// "Original | Network A" or "Revised | Network A - Q3 update" ("Revised | Network A" when
// the revision has no name)
export const formatRosterLabel = (roster: RosterLabelFields): string => {
    const {kind, rosterName, revisionName} = rosterLabelParts(roster);
    return `${kind}${KIND_SEPARATOR}${rosterName}${revisionName ? `${REVISION_SEPARATOR}${revisionName}` : ''}`;
};
